import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Cite from '../components/Cite';
import { useLanguage } from '../../../hooks/useLanguage';

const ResearchWorldview: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? "Working on something where the substrate matters? Let's compare notes."
          : 'Werk je aan iets waarbij de AI-laag ertoe doet? Laten we kennis vergelijken.'}
      </h2>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  return (
    <WebsiteLayout customCta={customCta}>
      <Helmet>
        <title>
          {en
            ? 'An LLM has a worldview — and we read it · Thoughtful Oasis'
            : 'Een LLM heeft een wereldbeeld — en we lezen het uit · Thoughtful Oasis'}
        </title>
        <meta
          name="description"
          content="First empirical demonstration that an LLM's internal world model can be projected into an interpretable vector ontology and verified — 50 genres, 47 phrasings, 15 million songs."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/research/worldview" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Thoughtful Oasis" />
        <meta
          property="og:title"
          content={
            en
              ? 'An LLM has a worldview — and we read it'
              : 'Een LLM heeft een wereldbeeld — en we lezen het uit'
          }
        />
        <meta
          property="og:description"
          content="First empirical demonstration that an LLM's internal world model can be projected into an interpretable vector ontology and verified — 50 genres, 47 phrasings, 15 million songs."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/research/worldview" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            en
              ? 'An LLM has a worldview — and we read it'
              : 'Een LLM heeft een wereldbeeld — en we lezen het uit'
          }
        />
        <meta
          name="twitter:description"
          content="First empirical demonstration that an LLM's internal world model can be projected into an interpretable vector ontology and verified — 50 genres, 47 phrasings, 15 million songs."
        />
        <meta name="twitter:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "ScholarlyArticle",
          "headline": "Vector Ontologies as an LLM World View Extraction Method",
          "author": [
            {"@type": "Person", "name": "Kaspar Rothenfusser"},
            {"@type": "Person", "name": "Bekk Blando"}
          ],
          "datePublished": "2025",
          "publisher": {"@type": "Organization", "name": "Thoughtful Oasis", "url": "https://thoughtful-oasis.com/"},
          "url": "https://thoughtful-oasis.com/research/worldview",
          "sameAs": "https://arxiv.org/abs/2506.13252"
        }`}</script>
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Research", "item": "https://thoughtful-oasis.com/research"},
            {"@type": "ListItem", "position": 2, "name": "Worldview extraction", "item": "https://thoughtful-oasis.com/research/worldview"}
          ]
        }`}</script>
      </Helmet>

      <section className="post">
        <div className="post-layout">
          <aside className="post-aside">
            <p className="post-aside__byline">
              Kaspar Rothenfusser &amp; Bekk Blando, 2025 ·{' '}
              <a
                className="link-quiet"
                href="https://arxiv.org/abs/2506.13252"
                target="_blank"
                rel="noopener noreferrer"
              >
                arXiv 2506.13252
              </a>{' '}
              ·{' '}
              <Link className="link-quiet" to="/research">
                {en ? 'All research' : 'Al het onderzoek'}
              </Link>
            </p>
            <Cite
              filename="rothenfusser-blando-2025-worldview"
              apa="Rothenfusser, K., & Blando, B. (2025). Vector ontologies as an LLM world view extraction method (arXiv:2506.13252). arXiv. https://arxiv.org/abs/2506.13252"
              bibtex={'@article{rothenfusser2025worldview,\n  author        = {Rothenfusser, Kaspar and Blando, Bekk},\n  title         = {Vector Ontologies as an {LLM} World View Extraction Method},\n  year          = {2025},\n  eprint        = {2506.13252},\n  archivePrefix = {arXiv},\n  primaryClass  = {cs.CL},\n  url           = {https://arxiv.org/abs/2506.13252}\n}'}
            />
          </aside>
          <article className="post__main">
            <p className="post__meta">
              {en
                ? 'Plain-language summary · Empirical · 2025'
                : 'Samenvatting in gewone taal · Empirisch · 2025'}
            </p>
            <h1>
              {en
                ? 'An LLM has a worldview. We figured out how to read it.'
                : 'Een LLM heeft een wereldbeeld. We hebben uitgevogeld hoe je het uitleest.'}
            </h1>
            <p className="post__lede">
              {en
                ? "Eight named axes, 15 million songs, no fine-tuning. The first empirical demonstration that an LLM's internal world model can be projected into an interpretable space — and verified."
                : 'Acht benoemde assen, 15 miljoen nummers, geen fine-tuning. De eerste empirische demonstratie dat het interne wereldmodel van een LLM in een interpreteerbare ruimte geprojecteerd — en geverifieerd — kan worden.'}
            </p>

            {en ? (
              <div className="post__body" lang="en">
                <p>
                  The{' '}
                  <Link className="link-inline" to="/research/vector-ontologies">
                    previous paper
                  </Link>{' '}
                  made the case that vector ontologies — spaces with named, interpretable axes — are the
                  substrate AI should be built on. That case is theoretical. This paper is the empirical
                  follow-up.
                </p>
                <p>Yes. Here is how we tested it.</p>

                <h2>The hypothesis</h2>
                <p>
                  An LLM trained on trillions of tokens knows a lot about the world. It knows how Beethoven
                  differs from Daft Punk. It knows that a heart attack is more urgent than a sprained ankle.
                  But none of that is explicit — it is distributed across billions of parameters.
                </p>
                <p>
                  We hypothesised: if you predefine a space with named axes and ask the LLM to place a concept
                  inside it, the placement will be <strong>consistent</strong> across different phrasings and{' '}
                  <strong>accurate</strong> against real-world data.
                </p>
                <p>If that holds, the black-box model becomes a tool for filling an interpretable structure with content.</p>

                <h2>Why music</h2>
                <p>
                  We needed a domain with built-in ground truth. Spotify exposes eight audio features per song
                  — <em>danceability, energy, acousticness, instrumentalness, liveness, valence, speechiness,
                  tempo</em> — derived deterministically from the audio waveform. Every song already has a
                  known position in eight-dimensional space.
                </p>
                <p>
                  That lets us ask an LLM where it places <em>post-punk</em> in the same space and check the
                  answer against 15 million real songs. The axes are external, the ground truth is
                  independent, the LLM is not fine-tuned. Whatever comes out has to be doing real work.
                </p>

                <h2>Setup</h2>
                <p>
                  We built an eight-axis vector ontology from the Spotify definitions. Each axis was split
                  into six bins, giving <code>6⁸ ≈ 1.7 million</code> possible cells. About 52% of those
                  cells contained at least one real song.
                </p>
                <p>
                  We picked 50 genres. For each genre we wrote 47 differently-phrased queries — <em>"Jazz"</em>{' '}
                  <em>"lets vibe with some jazz"</em> etc. — and recorded the LLM's placement each time using{' '}
                  <code>GPT-4o-mini</code>, no fine-tuning.
                </p>

                <h2>Consistency</h2>
                <p>
                  Across 47 phrasings, the LLM landed in an average of <strong>12.5 unique cells</strong> out
                  of 1.7 million. Some genres had as few as 4. The placements occupied{' '}
                  <strong>0.0019%</strong> of the space's volume. A random baseline occupied{' '}
                  <strong>48%</strong>. That is roughly 25,000 times tighter clustering.
                </p>
                <p>
                  When you ask the model the same question 47 different ways, it gives you essentially the
                  same answer. The LLM has a stable position for <em>jazz</em>. It does not drift when you
                  change the wording.
                </p>

                <h2>Accuracy</h2>
                <p>
                  Stable is not the same as correct. Does the LLM's position for <em>jazz</em> match where
                  jazz actually lives in the space?
                </p>
                <p>
                  It does. After shifting the data to control for trivial baseline similarity, cosine
                  similarity between LLM centroids and ground-truth centroids was <strong>0.88</strong>{' '}
                  versus <strong>0.64</strong> for random. Euclidean distance: <strong>0.46</strong> vs.{' '}
                  <strong>0.66</strong>. Consistently and significantly closer than chance.
                </p>
                <figure className="post__figure">
                  <img
                    src="/assets/website/photos/papers/heatmap-trance.webp"
                    alt="PCA projection showing the LLM's placements for trance (blue dots) overlaid on the ground-truth distribution of trance songs (heatmap). The LLM clusters tightly inside the densest region."
                    loading="lazy"
                  />
                  <figcaption>
                    Trance — blue dots are the LLM's placements across 47 phrasings; the heatmap is the
                    actual distribution of trance songs in PCA space. The LLM lands where the music is.
                  </figcaption>
                </figure>
                <figure className="post__figure">
                  <img
                    src="/assets/website/photos/papers/heatmap-metal.webp"
                    alt="PCA projection showing the LLM's placements for metal (blue dots) overlaid on the ground-truth distribution of metal songs (heatmap). The LLM clusters along the densest band."
                    loading="lazy"
                  />
                  <figcaption>
                    Metal — same setup. The LLM's positions overlap with the ground-truth concentration of
                    metal songs.
                  </figcaption>
                </figure>

                <h2>What this means</h2>
                <p>
                  The world-model an LLM acquires during training is not just noise — it carries structure
                  that can be projected into an interpretable space and verified against ground truth. The
                  paper shows that if you define the space, a standard LLM will populate it consistently and
                  accurately, without fine-tuning.
                </p>
                <p>
                  The load-bearing decision is <em>choosing the space</em> — which axes, which dimensions,
                  which concepts a domain expert recognises. Once the axes are set, the LLM's learned
                  ontology can be extracted into them.
                </p>
                <blockquote>
                  LLMs contain extractable, structured knowledge that can be projected onto interpretable
                  frameworks defined by humans.
                </blockquote>

                <h2>Limitations and what follows</h2>
                <p>
                  The experiment is single-domain. Music has convenient properties — rich ground truth,
                  well-defined axes, public data. Other domains will be harder. The space was also fixed up
                  front; in practice, choosing the right axes is itself a process that the paper flags as
                  future work.
                </p>
                <p>
                  But the central question has been answered: vector ontologies as a method for extracting
                  structured worldviews from LLMs work. The substrate is real, populatable, and verifiable.
                </p>
                <p>
                  Technical details, distribution plots, and hypercube visualisations are in the{' '}
                  <a
                    className="link-inline"
                    href="https://arxiv.org/abs/2506.13252"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    original paper on arXiv
                  </a>
                  . Code and data are on{' '}
                  <a
                    className="link-inline"
                    href="https://github.com/Thoughtful-Oasis/LLM-Worldview-Extraction-using-vector-ontologies"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub
                  </a>
                  . For the theoretical framework underneath, see the{' '}
                  <Link className="link-inline" to="/research/vector-ontologies">
                    first paper
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <div className="post__body" lang="nl">
                <p>
                  De{' '}
                  <Link className="link-inline" to="/research/vector-ontologies">
                    vorige paper
                  </Link>{' '}
                  maakte de zaak dat vector-ontologieën — ruimtes met benoemde, interpreteerbare assen — de
                  AI-laag zijn waarop AI gebouwd zou moeten worden. Die zaak is theoretisch. Deze paper is de
                  empirische vervolgstap.
                </p>
                <p>Ja. Zo hebben we het getest.</p>

                <h2>De hypothese</h2>
                <p>
                  Een LLM getraind op triljoenen tokens weet veel over de wereld. Het weet hoe Beethoven
                  verschilt van Daft Punk. Het weet dat een hartaanval urgenter is dan een verstuikte enkel.
                  Maar niets daarvan is expliciet — het zit verdeeld over miljarden parameters.
                </p>
                <p>
                  Onze hypothese: als je een ruimte met benoemde assen vooraf definieert en het LLM vraagt een
                  concept erin te plaatsen, is die plaatsing <strong>consistent</strong> over verschillende
                  formuleringen en <strong>accuraat</strong> ten opzichte van echte data.
                </p>
                <p>Als dat klopt, wordt het black-box-model een tool om een interpreteerbare structuur met inhoud te vullen.</p>

                <h2>Waarom muziek</h2>
                <p>
                  We hadden een domein nodig met ingebouwde grondwaarheid. Spotify ontsluit acht audio-kenmerken
                  per nummer — <em>danceability, energy, acousticness, instrumentalness, liveness, valence,
                  speechiness, tempo</em> — deterministisch afgeleid uit het audiosignaal. Elk nummer heeft al
                  een bekende positie in een acht-dimensionale ruimte.
                </p>
                <p>
                  Daardoor kun je een LLM vragen waar het <em>post-punk</em> plaatst in diezelfde ruimte en het
                  antwoord toetsen aan 15 miljoen echte nummers. De assen zijn extern gedefinieerd, de
                  grondwaarheid is onafhankelijk, het LLM is niet fine-tuned. Wat er ook uitkomt, het moet echt
                  werk verrichten.
                </p>

                <h2>Opzet</h2>
                <p>
                  We bouwden een vector-ontologie met acht assen op basis van Spotify-definities. Elke as werd
                  opgesplitst in zes bins, wat <code>6⁸ ≈ 1,7 miljoen</code> mogelijke cellen opleverde.
                  Ongeveer 52% daarvan bevatte minstens één echt nummer.
                </p>
                <p>
                  We kozen 50 genres. Per genre schreven we 47 anders geformuleerde queries — <em>"waar zou je
                  jazz plaatsen,"</em> <em>"beschrijf jazz in termen van deze audio-features,"</em> enz. — en
                  legden we elke plaatsing vast met <code>GPT-4o-mini</code>, zonder fine-tuning.
                </p>

                <h2>Consistentie</h2>
                <p>
                  Over 47 formuleringen kwam het LLM uit in gemiddeld <strong>12,5 unieke cellen</strong> op
                  1,7 miljoen. Sommige genres hadden er maar 4. De plaatsingen besloegen{' '}
                  <strong>0,0019%</strong> van het volume. Een random baseline besloeg <strong>48%</strong>.
                  Ruwweg 25.000 keer strakker geclusterd.
                </p>
                <p>
                  Als je het model dezelfde vraag op 47 manieren stelt, geeft het in essentie hetzelfde
                  antwoord. Het LLM heeft een stabiele positie voor <em>jazz</em>. Het zwerft niet rond als je
                  de formulering verandert.
                </p>

                <h2>Accuratesse</h2>
                <p>
                  Stabiel is niet hetzelfde als correct. Komt de positie van het LLM voor <em>jazz</em>{' '}
                  overeen met waar jazz echt leeft in de ruimte?
                </p>
                <p>
                  Ja. Na verschuiving van de data om triviale baseline-gelijkenis weg te filteren:
                  cosinus-gelijkenis tussen LLM-zwaartepunten en grondwaarheid-zwaartepunten was{' '}
                  <strong>0,88</strong> versus <strong>0,64</strong> voor random. Euclidische afstand:{' '}
                  <strong>0,46</strong> vs. <strong>0,66</strong>. Consistent en significant beter dan kans.
                </p>
                <figure className="post__figure">
                  <img
                    src="/assets/website/photos/papers/heatmap-trance.webp"
                    alt="PCA-projectie met de plaatsingen van het LLM voor trance (blauwe punten) over de grondwaarheid-verdeling van trance-nummers (heatmap). Het LLM clustert strak in het dichtste gebied."
                    loading="lazy"
                  />
                  <figcaption>
                    Trance — blauwe punten zijn de plaatsingen van het LLM over 47 formuleringen; de heatmap
                    is de werkelijke verdeling van trance-nummers in PCA-ruimte. Het LLM landt waar de muziek
                    is.
                  </figcaption>
                </figure>
                <figure className="post__figure">
                  <img
                    src="/assets/website/photos/papers/heatmap-metal.webp"
                    alt="PCA-projectie met de plaatsingen van het LLM voor metal (blauwe punten) over de grondwaarheid-verdeling van metal-nummers (heatmap). Het LLM clustert langs de dichtste band."
                    loading="lazy"
                  />
                  <figcaption>
                    Metal — dezelfde opzet. De posities van het LLM overlappen met de
                    grondwaarheid-concentratie van metal-nummers.
                  </figcaption>
                </figure>

                <h2>Wat dit betekent</h2>
                <p>
                  Het wereldmodel dat een LLM tijdens training opbouwt is geen ruis — het draagt structuur die
                  in een interpreteerbare ruimte geprojecteerd en tegen grondwaarheid geverifieerd kan worden.
                  De paper laat zien dat als je de ruimte definieert, een standaard-LLM die consistent en
                  accuraat invult, zonder fine-tuning.
                </p>
                <p>
                  De dragende beslissing is <em>de ruimte kiezen</em> — welke assen, welke dimensies, welke
                  concepten een domeinexpert herkent. Zodra de assen vaststaan, kan de geleerde ontologie van
                  het LLM erin geëxtraheerd worden.
                </p>
                <blockquote>
                  LLMs bevatten extraheerbare, gestructureerde kennis die geprojecteerd kan worden op
                  interpreteerbare kaders gedefinieerd door mensen.
                </blockquote>

                <h2>Beperkingen en vervolg</h2>
                <p>
                  Het experiment is single-domain. Muziek heeft handige eigenschappen — rijke grondwaarheid,
                  goed gedefinieerde assen, publieke data. Andere domeinen worden lastiger. De ruimte stond
                  ook vooraf vast; in de praktijk is de keuze van de juiste assen zelf een proces dat de
                  paper als toekomstig werk aanmerkt.
                </p>
                <p>
                  Maar de centrale vraag is beantwoord: vector-ontologieën als methode om gestructureerde
                  wereldbeelden uit LLMs te halen, werken. De AI-laag is reëel, vulbaar en verifieerbaar.
                </p>
                <p>
                  Technische details, distributie-plots en hyperkubus-visualisaties staan in de{' '}
                  <a
                    className="link-inline"
                    href="https://arxiv.org/abs/2506.13252"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    originele paper op arXiv
                  </a>
                  . Code en data staan op{' '}
                  <a
                    className="link-inline"
                    href="https://github.com/Thoughtful-Oasis/LLM-Worldview-Extraction-using-vector-ontologies"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub
                  </a>
                  . Het theoretische kader eronder staat in de{' '}
                  <Link className="link-inline" to="/research/vector-ontologies">
                    eerste paper
                  </Link>
                  .
                </p>
              </div>
            )}
          </article>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default ResearchWorldview;
